import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useAppSelector } from '../../hooks/redux';
import { useAssociateExternalDomainMutation } from '../../api/pagesApi';

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: ${({ isOpen }) => isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  padding: 24px 24px 0 24px;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 8px 0;
`;

const ModalSubtitle = styled.p`
  color: #718096;
  margin: 0 0 16px 0;
  font-size: 14px;
`;

const ModalBody = styled.div`
  padding: 0 24px 24px 24px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const RequiredLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #4a5568;
  
  &:after {
    content: " *";
    color: #e53e3e;
  }
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }
  
  &:invalid {
    border-color: #e53e3e;
  }
`;

const HelpText = styled.p`
  color: #718096;
  font-size: 12px;
  margin: 0;
  line-height: 1.4;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
  
  ${({ variant = 'secondary' }) => {
    if (variant === 'primary') {
      return `
        background: #4299e1;
        color: white;
        &:hover:not(:disabled) {
          background: #3182ce;
        }
        &:disabled {
          background: #a0aec0;
          cursor: not-allowed;
        }
      `;
    }
    return `
      background: white;
      color: #4a5568;
      border-color: #e2e8f0;
      &:hover {
        background: #f7fafc;
      }
    `;
  }}
`;

const ErrorMessage = styled.div`
  background: #fed7d7;
  color: #742a2a;
  padding: 12px;
  border-radius: 6px;
  border-left: 4px solid #e53e3e;
  font-size: 14px;
  margin-bottom: 16px;
`;

const SuccessMessage = styled.div`
  background: #c6f6d5;
  color: #22543d;
  padding: 12px;
  border-radius: 6px;
  border-left: 4px solid #38a169;
  font-size: 14px;
  margin-bottom: 16px;
`;

interface CustomDomainModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (data: any) => void;
}

export const CustomDomainModal: React.FC<CustomDomainModalProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess 
}) => {
  const { user } = useAppSelector((state) => state.auth);
  const [associateExternalDomain, { isLoading, error: apiError }] = useAssociateExternalDomainMutation();
  const [success, setSuccess] = useState<string | null>(null);
  const [customDomain, setCustomDomain] = useState('');

  const validateDomain = (domain: string): boolean => {
    const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return domainRegex.test(domain) && domain.length <= 253;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.customerId) {
      return;
    }

    if (!customDomain.trim()) {
      return;
    }

    if (!validateDomain(customDomain.trim())) {
      return;
    }

    setSuccess(null);

    try {
      const result = await associateExternalDomain({
        customerId: user.customerId,
        data: { customDomain: customDomain.trim() }
      }).unwrap();

      setSuccess(result.message);
      
      setTimeout(() => {
        onSuccess?.(result);
        handleClose();
      }, 2000);

    } catch (err) {
      console.error('Failed to associate custom domain:', err);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setCustomDomain('');
      setSuccess(null);
      onClose();
    }
  };

  const getErrorMessage = () => {
    if (!apiError) return null;
    
    if ('data' in apiError && apiError.data && typeof apiError.data === 'object') {
      const errorData = apiError.data as any;
      return errorData.error || errorData.message || 'Failed to associate custom domain. Please try again.';
    }
    
    return 'Failed to associate custom domain. Please try again.';
  };

  return (
    <Overlay isOpen={isOpen} onClick={handleClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Associate Custom Domain</ModalTitle>
          <ModalSubtitle>
            Create a landing page associated with your custom domain for external redirects and integrations.
          </ModalSubtitle>
        </ModalHeader>
        
        <ModalBody>
          {getErrorMessage() && (
            <ErrorMessage>
              {getErrorMessage()}
            </ErrorMessage>
          )}
          
          {success && (
            <SuccessMessage>
              {success}
            </SuccessMessage>
          )}
          
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <RequiredLabel htmlFor="customDomain">Custom Domain</RequiredLabel>
              <Input
                id="customDomain"
                name="customDomain"
                type="text"
                required
                value={customDomain}
                onChange={(e) => setCustomDomain(e.target.value)}
                placeholder="example-company.com"
                disabled={isLoading}
              />
              <HelpText>
                Enter your domain name (e.g., example-company.com). This will create a dummy landing page 
                with template 'external' and generate a unique slug based on your domain.
              </HelpText>
            </FormGroup>
            
            <ButtonGroup>
              <Button type="button" onClick={handleClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={isLoading}>
                {isLoading ? 'Creating...' : 'Associate Domain'}
              </Button>
            </ButtonGroup>
          </Form>
        </ModalBody>
      </Modal>
    </Overlay>
  );
};